import { startOfDay, endOfDay, addSeconds } from 'date-fns';
import { Task } from '../types/task';

/**
 * 判断任务是否应该在指定日期显示
 * 
 * NOTE: 这个逻辑是 fetchMem.ts 的超集，包含了更全面的时间判断
 * - fetchMem 在智能匹配模式下只考虑 startAt + cost
 * - fetchMem 在指定字段模式下只考虑单个时间字段
 * - TaskV2 综合考虑所有时间字段，让用户看到完整的日程
 * - 如果用户反馈不一致，可以考虑简化逻辑
 */
export function shouldTaskAppearOnDate(task: Task, targetDate: Date): boolean {
  const targetStart = startOfDay(targetDate);
  const targetEnd = endOfDay(targetDate);
  
  // === 与 fetchMem 智能匹配模式一致的核心逻辑 ===
  
  // 1. "点"类任务：只有 startAt，没有 cost
  if (task.startAt && (!task.cost || task.cost === 0)) {
    const taskStart = new Date(task.startAt);
    if (taskStart >= targetStart && taskStart <= targetEnd) {
      return true;
    }
  }
  
  // 2. "块"类任务：有 startAt 和 cost  
  if (task.startAt && task.cost && task.cost > 0) {
    const taskStart = new Date(task.startAt);
    const taskEnd = addSeconds(taskStart, task.cost);
    
    // 区间交集判断（与 fetchMem 完全一致）
    // 任务结束时间 >= 目标开始时间 && 任务开始时间 <= 目标结束时间
    if (taskEnd >= targetStart && taskStart <= targetEnd) {
      return true;
    }
  }
  
  // === TaskV2 扩展逻辑（fetchMem 的超集） ===
  
  // 3. 截止日期：dueAt 在目标日期（相当于 fetchMem 的 timeField='dueAt' 模式）
  if (task.dueAt && !task.done) {
    const dueDate = new Date(task.dueAt);
    if (dueDate >= targetStart && dueDate <= targetEnd) {
      return true;
    }
  }
  
  // 4. 完成时间：doneAt 在目标日期（包括 undone 后的任务）
  if (task.doneAt) {
    const doneDate = new Date(task.doneAt);
    if (doneDate >= targetStart && doneDate <= targetEnd) {
      return true;
    }
  }
  
  return false;
}

/**
 * 判断任务是否"真正"属于指定日期
 * 只考虑 startAt、cost、dueAt，不考虑 doneAt
 */
export function isTaskNaturallyOnDate(task: Task, targetDate: Date): boolean {
  const targetStart = startOfDay(targetDate);
  const targetEnd = endOfDay(targetDate);

  // 1. "点"类任务：只有 startAt，没有 cost
  if (task.startAt && (!task.cost || task.cost === 0)) {
    const taskStart = new Date(task.startAt);
    if (taskStart >= targetStart && taskStart <= targetEnd) {
      return true;
    }
  }

  // 2. "块"类任务：有 startAt 和 cost
  if (task.startAt && task.cost && task.cost > 0) {
    const taskStart = new Date(task.startAt);
    const taskEnd = addSeconds(taskStart, task.cost);

    if (taskEnd >= targetStart && taskStart <= targetEnd) {
      return true;
    }
  }

  // 3. 截止日期：dueAt 在目标日期（只对未完成任务）
  if (task.dueAt && !task.done) {
    const dueDate = new Date(task.dueAt);
    if (dueDate >= targetStart && dueDate <= targetEnd) {
      return true;
    }
  }

  return false;
}

/**
 * 判断任务是否因为完成时间而显示在指定日期
 * 注意：包括已完成和 undone 后的任务（只要有 doneAt 就算）
 */
export function isTaskShownByDoneAt(task: Task, targetDate: Date): boolean {
  // 只要有完成时间记录就算，不管当前是否为完成状态
  if (!task.doneAt) {
    return false;
  }

  const targetStart = startOfDay(targetDate);
  const targetEnd = endOfDay(targetDate);
  const doneDate = new Date(task.doneAt);

  return doneDate >= targetStart && doneDate <= targetEnd;
}
